import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useStencils } from "../../lib/hooks/useStencils";
import { useCategories } from "../../lib/hooks/useCategories";
import { useEvent } from "../../lib/hooks/useEvent";
import QuickSelectModal from "../components/StencilSelection/QuickSelect";
import StencilSidebar from "../components/StencilSelection/StencilSidebar";
import Search from "../components/Search/Search";
import StencilForm from "../components/StencilSelection/StencilForm";
import CreateCategoryForm from "../components/StencilSelection/CreateCategoryForm";
import DeleteCategory from "../components/StencilSelection/DeleteCategory";
import UpdateCategoryForm from "../components/StencilSelection/UpdateCategoryForm";
import FullScreenImg from "../components/StencilSelection/FullScreenImg";
import RenderCategories from "../components/StencilSelection/RenderCategories.jsx";
import RenderStencils from "../components/StencilSelection/RenderStencils.jsx";
import RenderWeeks from "../components/StencilSelection/RenderWeeks.jsx";
import CategoryButtons from "../components/StencilSelection/CategoryButtons.jsx";
import Navbar from "../components/Navbar/Navbar.jsx";
import { adminStatus } from "../../lib/services/authService.js";
import PrintPopup from "../components/StencilSelection/PrintPopup.jsx";
import PrintIcon from "../../assets/print_icon.png";
import { stencilFilter } from "../../lib/utils/stencilUtils.js";
import ConfirmStencil from "../components/StencilSelection/ConfirmStencil.jsx";
import { sortStencils } from "../../lib/services/helperService.js";

const StencilSelection = ({
  isAdminAuthenticated,
  setIsAdminAuthenticated,
}) => {
  // filtering useStates
  const [keywords, setKeywords] = useState([]);
  const [filteredSelection, setFilteredSelection] = useState([]);
  const [shownSelection, setShownSelection] = useState([]);
  const [query, setQuery] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [weeks, setWeeks] = useState("none");
  const [showArchived, setShowArchived] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);

  // pulled from db
  const { stencils, getStencils, setStencils } = useStencils();
  const { categories, getCategories } = useCategories();
  const { event, getEvent } = useEvent();

  // react form
  const { reset } = useForm();

  // States for determining modal popups
  const [selectedStencil, setSelectedStencil] = useState(null);
  const [isAddingStencil, setIsAddingStencil] = useState(false);
  const [isDeletingStencil, setIsDeletingStencil] = useState(false);
  const [isArchivingStencil, setIsArchivingStencil] = useState(false);
  const [isEditingStencil, setIsEditingStencil] = useState(false);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [isDeletingCategory, setIsDeletingCategory] = useState(false);
  const [isEditingCategory, setIsEditingCategory] = useState(false);
  const [isSelected, setIsSelected] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [showQuickSelect, setShowQuickSelect] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);

  // References to track grid position
  const gridRef = useRef(null);

  useEffect(() => {
    const checkAdminStatus = async () => {
      const response = await adminStatus();
      if (response.ok) {
        const data = await response.json();
        setIsAdminAuthenticated(data.isAuthenticated);
      }
    };

    checkAdminStatus();
  });

  // Get stencils, categories, current event, and stencilWeeks from database if store not yet set
  useEffect(() => {
    if (stencils?.length === 0) {
      getStencils();
    }
    if (categories?.length === 0) {
      getCategories();
    }
    if (!event || Object.keys(event).length === 0) {
      getEvent();
    }
    setIsHydrated(true);
  }, [
    stencils.length,
    categories.length,
    event,
    getStencils,
    getCategories,
    getEvent,
  ]);

  useEffect(() => {
    const sortedStencils = sortStencils(stencils);
    setStencils(sortedStencils);
  }, [stencils]);

  useEffect(() => {
    reset({});
  }, []);

  // set keywords to the whole list of stencils initially
  useEffect(() => {
    if (isHydrated) {
      const shownStencils = stencils.filter((s) => showArchived || !s.archived);
      setShownSelection(shownStencils);
    }
  }, [stencils, isHydrated, showArchived]);

  // when either selectedCategories, weeks, search query, or filteredSelection changes, filter:
  useEffect(() => {
    if (shownSelection.length !== 0) {
      // determine whether the filteredSelection is important to consider:
      let searching = !query || query.trim() === "" ? 0 : 1;
      stencilFilter(
        shownSelection,
        filteredSelection,
        weeks,
        selectedCategories,
        setKeywords,
        searching
      );
    }
  }, [
    weeks,
    selectedCategories,
    query,
    filteredSelection,
    showArchived,
    shownSelection,
  ]);

  // closes stencil sidebar when no stencil is selected
  const closeSidebar = () => {
    setSelectedStencil(null);
    setIsSelected(false);
  };

  async function refreshStencils() {
    await getStencils();
  }

  return (
    <div>
      <Navbar
        isAdminAuthenticated={isAdminAuthenticated}
        setIsAdminAuthenticated={setIsAdminAuthenticated}
        location={"/admin/stencil-selection"}
      />
      <div
        className="container-fluid row mx-auto"
        style={{ paddingTop: "80px" }}
      >
        <div style={{ display: "flex", marginBottom: "10px" }}>
          <RenderWeeks setWeeks={setWeeks} weeks={weeks} />
          <div className="col-6" style={{ textAlign: "right" }}>
            <div className="d-flex align-center gap-4 px-5 search-bar">
              <Search
                query={query}
                setQuery={setQuery}
                range={stencils}
                setProduct={setFilteredSelection}
              />
              <button onClick={() => setIsPrinting(true)}>
                <img src={PrintIcon} width={36} alt="Print" />
              </button>
            </div>
          </div>

          <div
            className="col-3"
            style={{ textAlign: "center", display: "flex", gap: "10px" }}
          >
            <button
              className="add-button round-button"
              style={{ padding: 10 }}
              onClick={() => setIsAddingStencil(true)}
            >
              New Stencil
            </button>
            <button
              className="round-button"
              style={{
                backgroundColor: "#ed4709",
                paddingLeft: 20,
                paddingRight: 20,
              }}
              onClick={() => setShowQuickSelect(true)}
            >
              Quick Select
            </button>
          </div>
        </div>
      </div>

      <div
        className="container-fluid row mx-auto"
        style={{ marginBottom: "5px" }}
      >
        <div className="select-stencil-sidebar col">
          <RenderCategories
            selectedCategories={selectedCategories}
            setSelectedCategories={setSelectedCategories}
          />
          <hr style={{ color: "white" }} />
          <CategoryButtons
            setShowArchived={setShowArchived}
            showArchived={showArchived}
            setIsAddingCategory={setIsAddingCategory}
            setIsDeletingCategory={setIsDeletingCategory}
            setIsEditingCategory={setIsEditingCategory}
          />
        </div>

        <div
          className={`stencil-selection-container mx-2 ${
            isSelected ? `col-7` : `col-10`
          }`}
        >
          <div className="scrollable-center container">
            <RenderStencils
              gridRef={gridRef}
              setKeywords={setKeywords}
              keywords={keywords}
              setIsSelected={setIsSelected}
              isSelected={isSelected}
              setSelectedStencil={setSelectedStencil}
              selectedStencil={selectedStencil}
            />
          </div>
        </div>

        {isSelected && (
          <StencilSidebar
            onClose={closeSidebar}
            onDelete={() => {
              if (selectedStencil) setIsDeletingStencil(true);
            }}
            onEdit={() => {
              if (selectedStencil) setIsEditingStencil(true);
            }}
            onArchive={() => {
              if (selectedStencil) setIsArchivingStencil(true);
            }}
            item={selectedStencil}
            setIsFullScreen={setIsFullScreen}
            setSelectedStencil={setSelectedStencil}
            selectedStencil={selectedStencil}
            setKeywords={setKeywords}
          />
        )}
      </div>

      {!!isFullScreen && (
        <FullScreenImg
          imgSrc={`/api/stencils/files/${selectedStencil.image_name}`}
          onClose={() => setIsFullScreen(false)}
        />
      )}

      {/* Stencil Modal popups */}
      {!!isAddingStencil && (
        <StencilForm
          stencil={selectedStencil}
          from="Create"
          onClose={() => setIsAddingStencil(false)}
          setProduct1={setKeywords}
          setProduct2={setFilteredSelection}
        />
      )}
      {!!isAddingCategory && (
        <CreateCategoryForm onClose={() => setIsAddingCategory(false)} />
      )}
      {!!isEditingStencil && (
        <StencilForm
          stencil={selectedStencil}
          setSelectedStencil={setSelectedStencil}
          from="Update"
          onClose={() => setIsEditingStencil(false)}
          setProduct1={setKeywords}
          setProduct2={setFilteredSelection}
        />
      )}
      {!!isEditingCategory && (
        <UpdateCategoryForm onClose={() => setIsEditingCategory(false)} />
      )}
      {!!isDeletingStencil && (
        <ConfirmStencil
          stencil={selectedStencil}
          onClose={() => setIsDeletingStencil(false)}
          onDelete={closeSidebar}
          setProduct1={setKeywords}
          setProduct2={setFilteredSelection}
        />
      )}
      {!!isArchivingStencil && (
        <ConfirmStencil
          setSelectedStencil={setSelectedStencil}
          stencil={selectedStencil}
          onClose={() => setIsArchivingStencil(false)}
          archiving={true}
          showArchived={showArchived}
          setShownSelection={setShownSelection}
        />
      )}
      {!!isDeletingCategory && (
        <DeleteCategory onClose={() => setIsDeletingCategory(false)} />
      )}
      {showQuickSelect && (
        <QuickSelectModal
          onClose={() => {
            setShowQuickSelect(false);
            refreshStencils();
          }}
        />
      )}
      {!!isPrinting && <PrintPopup onClose={() => setIsPrinting(false)} />}
    </div>
  );
};

export default StencilSelection;
